import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, ExternalLink, Hammer, FileText, Building2 } from 'lucide-react';
import { useAuth } from '@/auth/hooks/useAuth';
import { useContributorDashboard } from '../hooks/useContributorDashboard';
import { MetricsSummaryCards } from '../components/MetricsSummaryCards';
import { ArcheMetricsTable } from '../components/ArcheMetricsTable';
import { PlatformOwnedPluginsSection } from '../components/PlatformOwnedPluginsSection';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

export function ContributorDashboard() {
  const { summary, arches, loading, error, refresh } = useContributorDashboard();
  const { user } = useAuth();
  // Mirrors `requireAdmin` on /v1/admin/plugins/platform-owned
  // (services/api-service/src/routes/plugins/platform-owned-admin.ts). The client
  // gate only hides UI; the server is the authorization boundary.
  const isAdmin = useMemo(() => user?.roles?.includes('admin') ?? false, [user?.roles]);

  return (
    <div>
      {/* Page Header */}
      <div className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold">Contributor Dashboard</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Monitor your arches' performance and revenue
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Button variant="outline" onClick={refresh} disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Refreshing...' : 'Refresh'}
              </Button>
              <Button variant="outline" asChild>
                <Link to="/marketplace">
                  View Arche Store
                  <ExternalLink className="h-4 w-4 ml-2" />
                </Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/submissions">
                  <FileText className="h-4 w-4 mr-2" />
                  My Submissions
                </Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/contributor/workspaces">
                  <Building2 className="h-4 w-4 mr-2" />
                  Workspace Assets
                </Link>
              </Button>
              <Button asChild>
                <Link to="/a/arche_plugin_builder">
                  <Hammer className="h-4 w-4 mr-2" />
                  Arche Builder
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {error ? (
          <Card className="border-destructive/50 bg-destructive/10 mb-8">
            <CardContent className="p-4">
              <p className="text-destructive">{error}</p>
              <Button
                variant="link"
                onClick={refresh}
                className="mt-2 p-0 h-auto text-destructive hover:text-destructive/80"
              >
                Try again
              </Button>
            </CardContent>
          </Card>
        ) : null}

        {/* Summary Cards */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold mb-4">Overview</h2>
          <MetricsSummaryCards summary={summary} loading={loading} />
        </section>

        {/* Arche Metrics Table */}
        <section>
          <h2 className="text-lg font-semibold mb-4">Your Arches</h2>
          <ArcheMetricsTable arches={arches} loading={loading} />
        </section>

        {/*
          Fias-owned (platform-owned) plugins — a SEPARATE section fed by a
          separate admin endpoint, so the Overview cards and "Your Arches" above
          stay numerically unchanged for admins. Platform revenue must never leak
          into a person's contributor metrics.
        */}
        {isAdmin && <PlatformOwnedPluginsSection />}
      </div>
    </div>
  );
}
